import React from 'react';
import { Globe, Shield, Zap, Server, Code, Layout } from 'lucide-react';

const IsometricWebsiteHosting = () => {
    // Standard Isometric rotation
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    return (
        <div className="w-full h-full flex items-center justify-center scale-75 md:scale-100">
            <div
                className="relative w-[280px] h-[280px]"
                style={sceneRotation}
            >
                {/* Main Base Layer */}
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Shadow */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-900/40 blur-2xl"
                        style={{
                            transform: 'translateZ(-60px) translateY(40px) translateX(40px)',
                        }}
                    />

                    {/* Thickness Slices (Blue Base) */}
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-700"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Top Face */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-600"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Subtle surface detail */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />

                        {/* Grid lines on base for "hosting" feel */}
                        <div className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                                backgroundSize: '40px 40px'
                            }}
                        />
                    </div>
                </div>

                {/* Surface Elements (Flat on the board) */}

                {/* Server Rack Representation (Back Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '20px',
                        top: '20px',
                        width: '80px',
                        height: '80px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 15 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-slate-800" style={{ transform: `translateZ(${i}px)` }} />
                    ))}
                    {/* Top */}
                    <div className="absolute inset-0 rounded-xl bg-slate-700 border border-slate-600 flex items-center justify-center" style={{ transform: 'translateZ(15px)' }}>
                        <Server className="w-8 h-8 text-blue-400" />
                    </div>
                    {/* Blinking lights on side */}
                    <div className="absolute -right-1 top-2 w-1 h-2 bg-green-400 rounded-full animate-pulse" style={{ transform: 'translateZ(10px)' }} />
                    <div className="absolute -right-1 top-5 w-1 h-2 bg-green-400 rounded-full animate-pulse delay-75" style={{ transform: 'translateZ(10px)' }} />
                </div>

                {/* Database/Storage (Back Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '30px',
                        top: '30px',
                        width: '60px',
                        height: '60px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Cylinder effect via stacked circles */}
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-full bg-blue-900" style={{ transform: `translateZ(${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 rounded-full bg-blue-600 border-4 border-blue-400" style={{ transform: 'translateZ(12px)' }} />
                </div>

                {/* Floating Elements */}

                {/* Code Bubble (Left) */}
                <div
                    className="absolute"
                    style={{
                        right: '160px',
                        bottom: '30px',
                        width: '60px',
                        height: '60px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-yellow-600" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-yellow-400 rounded-xl flex items-center justify-center shadow-lg border border-yellow-200">
                        <Code className="w-8 h-8 text-yellow-900" />
                    </div>
                </div>

                {/* Shield/Security (Right) */}
                <div
                    className="absolute"
                    style={{
                        left: '120px',
                        top: '-10px',
                        width: '60px',
                        height: '60px',
                        transform: 'translateZ(60px) rotateZ(-3deg) rotateX(-90deg)', // Standing up
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-green-700" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-green-500 rounded-xl flex items-center justify-center shadow-lg border border-green-300">
                        <Shield className="w-8 h-8 text-white" />
                    </div>
                </div>

                {/* MAIN ELEMENT: HTML Webpage Window */}
                <div
                    className="absolute"
                    style={{
                        left: '10%',
                        top: '100%',
                        width: '180px',
                        height: '140px',
                        marginLeft: '-90px',
                        marginTop: '-70px',
                        transform: 'translateZ(130px) rotateZ(-65deg) rotateX(-90deg) rotateY(25deg)', // Angled phone
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Window Thickness */}
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-lg bg-gray-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}

                    {/* Window Front */}
                    <div className="absolute inset-0 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
                        {/* Browser Bar */}
                        <div className="h-6 bg-gray-100 border-b border-gray-200 flex items-center px-2 gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                            <div className="ml-2 flex-1 h-3 bg-white rounded-sm border border-gray-200" />
                        </div>

                        {/* Website Content */}
                        <div className="flex-1 p-3 bg-white relative overflow-hidden">
                            {/* Header */}
                            <div className="flex justify-between items-center mb-3">
                                <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                                    <Layout className="w-5 h-5 text-blue-500" />
                                </div>
                                <div className="flex gap-2">
                                    <div className="w-12 h-2 bg-gray-100 rounded" />
                                    <div className="w-8 h-2 bg-gray-100 rounded" />
                                </div>
                            </div>

                            {/* Hero Area */}
                            <div className="w-full h-16 bg-blue-50 rounded-lg mb-3 flex items-center justify-center">
                                <Globe className="w-8 h-8 text-blue-200" />
                            </div>

                            {/* Content Lines */}
                            <div className="space-y-2">
                                <div className="w-full h-2 bg-gray-100 rounded" />
                                <div className="w-3/4 h-2 bg-gray-100 rounded" />
                                <div className="flex gap-2 mt-2">
                                    <div className="w-1/2 h-16 bg-gray-50 rounded" />
                                    <div className="w-1/2 h-16 bg-gray-50 rounded" />
                                </div>
                            </div>

                            {/* Floating "Live" Badge */}
                            <div className="absolute top-2 right-2 bg-green-100 text-green-700 text-[8px] font-bold px-1.5 py-0.5 rounded-full border border-green-200 flex items-center gap-1">
                                <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse" />
                                LIVE
                            </div>
                        </div>
                    </div>
                </div>

                {/* Speed/Zap Floating Element (Front) */}
                <div
                    className="absolute"
                    style={{
                        right: '20px',
                        bottom: '20px',
                        width: '40px',
                        height: '40px',
                        transform: 'translateZ(30px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-full bg-orange-600" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-orange-500 rounded-full flex items-center justify-center shadow-lg border border-orange-300">
                        <Zap className="w-6 h-6 text-white" />
                    </div>
                </div>

            </div>
        </div>
    );
};

export default IsometricWebsiteHosting;
